import storeManager from '../store/StoreManager';
import uiConstants from '../constants/UIConstants';
import routeTypes from '../constants/RouteTypes';
import marketPlaces from '../constants/MarketPlaces';
import uiActions from '../actions/UIActions';
import sessionReader from '../store/readHelpers/SessionReader';
import settingsReader from '../store/readHelpers/SettingsReader';
import apiReader from '../store/readHelpers/ApiReader';
import sessionConstants from '../constants/SessionConstants';
import marketplaceSettingsService from './MarketplaceSettingsService';
import studentHighlightsService from './StudentHighlightsService';
import utilService from './UtilService';
import i18nService from './i18nService';
import remoteOralExamService from './RemoteOralExamService';
import tocReader from '../store/readHelpers/TocReader';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import navigationService from './NavigationService';
import responseReader from '../store/readHelpers/ResponseReader';
import branchRuleReader from '../store/readHelpers/BranchRuleReader';
import marketplacePropertiesService from './MarketplacePropertiesService';
import themeServices from '../themes/ThemeServices';
import cambridgeThemeVariables from '../themes/themeVariables/cambridge';
import cambridgeInnovationThemeVariables from '../themes/themeVariables/cambridgeInnovation';
import fjordThemeVariables from '../themes/themeVariables/fjord';
import ceqThemeVariables from '../themes/themeVariables/ceq';
import defaultThemeVariables from '../themes/themeVariables/default';
import backgroundAudioService from './BackgroundAudioService';

const uiService = {
    setCurrentViewSize(uiProp) {
        const { DESKTOP, MOBILE, TABLET } = uiConstants.VIEW_SIZE;
        const windowSize = window.innerWidth;
        let viewSize;
        if (windowSize <= uiConstants.BREAKPOINT_MOBILE) {
            viewSize = MOBILE;
        } else if (windowSize > uiConstants.BREAKPOINT_MOBILE && windowSize <= uiConstants.BREAKPOINT_DESKTOP) {
            viewSize = TABLET;
        } else {
            viewSize = DESKTOP;
        }
        if (uiProp.viewSize !== viewSize) {
            storeManager.dispatch(uiActions.uiWindowResized(viewSize));
        }
    },

    getCurrentIOSOrientation() {
        const { LANDSCAPE, PORTRAIT } = uiConstants.ORIENTATION;
        return window.innerWidth > window.innerHeight ? LANDSCAPE : PORTRAIT;
    },

    setScreenIOSOrientation() {
        const orientation = this.getCurrentIOSOrientation();
        storeManager.dispatch(uiActions.uiWindowOrientation(orientation));
    },

    getThemeVariables(mpTheme) {
        switch (mpTheme) {
        case marketPlaces.cambridge:
            return { ...defaultThemeVariables, ...cambridgeThemeVariables };
        case marketPlaces.cambridge_innovation:
            return { ...defaultThemeVariables, ...cambridgeInnovationThemeVariables };
        case marketPlaces.ceq:
            return { ...defaultThemeVariables, ...ceqThemeVariables };
        case marketPlaces.fjord:
            return { ...defaultThemeVariables, ...fjordThemeVariables };
        default:
            return defaultThemeVariables;
        }
    },

    removeThemesFromBody(body) {
        Array
            .from(body.classList)
            .forEach((className) => {
                if (className.indexOf('-theme') > -1) {
                    body.classList.remove(className);
                }
            });
    },

    getDirection(language) {
        return i18nService.RTLDirectionLanguages.includes(language) ? 'rtl' : 'ltr';
    },

    setDirection(language) {
        const direction = this.getDirection(language);
        storeManager.dispatch(uiActions.uiSetDirection(direction));
    },

    setTestPlayerStyle() {
        const body = document.getElementsByTagName('body')[0];
        const testPlayerStyle = settingsReader.getTestPlayerStyle();
        const { ui } = storeManager.getState();

        if (!testPlayerStyle) {
            uiService.setZoomFactor(uiConstants.ZOOM_FACTOR.SMALL);
            this.setTestPlayerTheme();
            return;
        }
        this.removeThemesFromBody(body);
        body.classList.add(`${testPlayerStyle}-theme`);

        const themeName = marketplacePropertiesService.getUiTheme();
        if (themeServices.isThemeOld(themeName)) {
            body.classList.add(`${testPlayerStyle}-old-theme`);
        }

        if (uiConstants.TEST_PLAYER_STYLES.includes(testPlayerStyle)) {
            uiService.setZoomFactor(uiConstants.ZOOM_FACTOR.LARGE);
        } else {
            uiService.setZoomFactor(ui.zoomFactor);
        }
    },

    setTestPlayerTheme() {
        const body = document.getElementsByTagName('body')[0];
        this.removeThemesFromBody(body);

        const marketplaceName = marketplaceSettingsService.getMarketplaceName(window.marketplaceName);
        const themeClassList = this.getThemeClassList(marketplaceName);
        body.classList.add(...themeClassList);
    },

    getThemeClassList(marketplaceName) {
        const classList = [];
        switch (marketplaceName) {
        case marketPlaces.cambridge:
        case marketPlaces.ceq:
        case marketPlaces.cxc:
        case marketPlaces.fjord:
            classList.push(`${marketplaceName}-theme`);
            break;
        case marketPlaces.cambridge_innovation:
            classList.push('fjord-theme');
            classList.push('cambridge-innovation-theme');
            break;
        default:
            classList.push('default-theme');
            break;
        }

        const themeName = marketplacePropertiesService.getUiTheme();
        if (themeServices.isThemeOld(themeName)) {
            classList.push(`${marketplaceName}-old-theme`);
        }

        return classList;
    },

    setZoomFactor(zoomFactor) {
        storeManager.dispatch(uiActions.uiSetZoomFactor(zoomFactor));
    },

    setCurrentMarketplaceTheme() {
        const marketplaceName = marketplaceSettingsService.getMarketplaceName(window.marketplaceName);
        const marketplaceTheme = Object.values(marketPlaces).includes(marketplaceName) ? marketplaceName : 'default';

        storeManager.dispatch(uiActions.uiSetMarketplaceTheme(marketplaceTheme));
        storeManager.dispatch(uiActions.uiSetThemeVariables(
            this.getThemeVariables(marketplaceTheme)
        ));
    },

    readAccessibilityContrastOptions() {
        const a11yContrastOptionString = window.marketplaceProperties.ACCESSIBILITY_CONTRAST_COLORS;
        const contrastOptions = ['normal'];
        if (a11yContrastOptionString?.length > 0) {
            a11yContrastOptionString.split(',').forEach((contrastOption) => {
                contrastOption.trim().toLowerCase();
                if (uiConstants.CONTRAST_OPTIONS.includes(contrastOption)) {
                    contrastOptions.push(contrastOption);
                }
            });
        }

        return contrastOptions;
    },

    shouldUseDarkMode() {
        const { ui } = storeManager.getState();
        return uiConstants.DARK_CONTRAST_COLORS.includes(ui.contrastOption);
    },

    showHeader(routeType, mode, viewMedia, context) {
        return sessionReader.isViewMediaScreen(viewMedia)
            && (sessionReader.isModeQuestionSet(mode) || sessionReader.isModeSection(mode))
            && !sessionReader.isContextViewGradingQuestion(context)
            && [
                routeTypes.ROUTE_TYPE_ALL,
                routeTypes.ROUTE_TYPE_AUTO_SUBMIT_OFFLINE,
                routeTypes.ROUTE_TYPE_ERROR,
                routeTypes.ROUTE_TYPE_MESSAGES,
                routeTypes.ROUTE_TYPE_OPTIONS,
                routeTypes.ROUTE_TYPE_LOGIN,
            ].indexOf(routeType) === -1;
    },

    showFooter(routeType, mode, viewMedia, context, timeHasExpired, hasAssessment = true) {
        return (
            !sessionReader.isLayoutParamShowAnswerKeys()
            && !timeHasExpired
            && hasAssessment
            && sessionReader.isViewMediaScreen(viewMedia)
            && (sessionReader.isModeQuestionSet(mode) || sessionReader.isModeSection(mode) || this.isQuestionWithFooterEnabled(mode))
            && !sessionReader.isContextViewGradingQuestion(context)
            && !(sessionReader.isRemoteOralExam() && !sessionReader.isContextReadinessCheck())
            && (
                [
                    '',
                    routeTypes.ROUTE_TYPE_TOC,
                    routeTypes.ROUTE_TYPE_SECTION,
                    routeTypes.ROUTE_TYPE_STIMULUS,
                    routeTypes.ROUTE_TYPE_QUESTION,
                    routeTypes.ROUTE_TYPE_DELIVERING,
                ].indexOf(routeType) !== -1
                || (routeType === routeTypes.ROUTE_TYPE_RESULT && !sessionReader.isContextExam(context))
            )
        );
    },

    isQuestionWithFooterEnabled(mode) {
        return sessionReader.isModeQuestion(mode) && marketplacePropertiesService.isQuestionFooterEnabled();
    },

    showAnnotationsSidebar() {
        return studentHighlightsService.isNoteTakingAndHighlightingEnabled() && sessionReader.isAnnotationsSidebarOpen() && !sessionReader.isPaused();
    },

    showChatSidebar() {
        return settingsReader.isChatEnabled() && sessionReader.isChatOpen();
    },

    showInvisibleNextButton(routeType, mode, viewMedia) {
        return (
            (sessionReader.isModeQuestionSet(mode) || sessionReader.isModeSection(mode))
            && sessionReader.isViewMediaPdf(viewMedia)
            && [
                routeTypes.ROUTE_TYPE_TOC,
                routeTypes.ROUTE_TYPE_SECTION,
                routeTypes.ROUTE_TYPE_QUESTION,
                routeTypes.ROUTE_TYPE_STIMULUS,
            ].indexOf(routeType) !== -1
        );
    },

    /**
     * Show additional resources task bar only if showing the footer
     * @param routeType
     * @param mode
     * @param viewMedia
     * @param context
     * @param timeHasExpired
     * @returns {boolean}
     */
    showAdditionalResourcesBar(routeType, mode, viewMedia, context, timeHasExpired, hasAssessment) {
        return (
            (
                this.showFooter(routeType, mode, viewMedia, context, timeHasExpired, hasAssessment)
                || routeType === routeTypes.ROUTE_TYPE_TEST_OVERVIEW
                || routeType === routeTypes.ROUTE_TYPE_ROOM_SCAN
            )
            && (sessionReader.questionSetHasAdditionalResources()
                || (settingsReader.isShowRubricForQuestionEnabled()
                    && sessionReader.questionSetHasRubricsMarking()))
            && !sessionReader.userHasChromeKiosk() // in chrome app we add the footer to control the windows
        );
    },

    shouldBlockUnloadWindow() {
        if (utilService.isHeadless()) {
            return false;
        }
        const lock = sessionReader.getNavigationLock();
        return (
            apiReader.hasUnsavedUserResponses()
            || lock.reason === sessionConstants.LOCK.FILE_UPLOAD_IN_PROGRESS
            || lock.reason === sessionConstants.LOCK.DELIVER_IN_PROGRESS
            || lock.reason === sessionConstants.LOCK.EVALUATION_IN_PROGRESS
        );
    },

    showComponentsDoneBox(allChildTestsFinished, timeHasExpired, remoteOralExam) {
        return (sessionReader.isParentTest() && (allChildTestsFinished || timeHasExpired))
            || (sessionReader.isRemoteOralExam() && remoteOralExamService.isTestCompleted(remoteOralExam));
    },

    // TODO: when IA1-6182 is done, need to check if we still need this
    isMathJax3Supported() {
        return !utilService.isSeb() || !utilService.isIOS();
    },

    showRecordingNotification(remoteOralExam) {
        return sessionReader.isRemoteOralExam() && remoteOralExamService.candidateInProgress(remoteOralExam);
    },

    disabledPreviousButton() {
        const { currentlyShown } = storeManager.getState();
        const firstQuestionInfo = tocReader.getFirstQuestionLocation();

        if (currentlyShown.scorableItem) {
            return (currentlyShown.questionId === firstQuestionInfo.contentRevisionId
                && !settingsReader.showTableOfContents()
                && currentlyShown.scorableItem === 1)
                || currentlyShown.route === '/';
        }
        return (currentlyShown.questionId === firstQuestionInfo.contentRevisionId && !settingsReader.showTableOfContents())
            || currentlyShown.route === '/';
    },

    disabledNextButton() {
        const { currentlyShown } = storeManager.getState();
        const question = assessmentReader.getQuestion(currentlyShown.questionId);

        // IA1-6496 - if a CAT test and high stakes auto delivery is enabled, next button should not be disabled.
        if (settingsReader.isCatTestType() && navigationService.isOnLastQuestion() && sessionReader.isAutoDeliverHighStakesEnabled()) {
            return false;
        }

        return navigationService.isOnFinalStepPage()
            || (navigationService.isOnLastQuestion() && !navigationService.isFinalStepAvailable() && !sessionReader.isContextReadinessCheck())
            || (settingsReader.hideNextButtonUntilAnswered() && currentlyShown.questionId
                && !navigationService.isOnTOCPage() && !responseReader.isQuestionAttempted(question))
            || (navigationService.isOnLastQuestion() && branchRuleReader.hasBranchRules() && (!apiReader.isOnline() || !apiReader.isServerOK()));
    },

    getElementStyleProperty(element, property) {
        if (!element || !property) {
            return null;
        }

        try {
            const style = window.getComputedStyle(element, null);
            return style.getPropertyValue(property);
        } catch (error) {
            return null;
        }
    },

    shouldUseSimplifiedSummaryPage() {
        return marketplacePropertiesService.isSimplifiedSummaryPageForPublicTestEnabled() && sessionReader.isPublicExam();
    },

    isVerticalLayout(section) {
        if (!section) return false;

        const { VERTICAL, VERTICAL_LEFT, VERTICAL_TOP } = uiConstants.SECTION_LAYOUT;
        return [VERTICAL, VERTICAL_LEFT, VERTICAL_TOP].includes(section.layout);
    },

    isSectionLayoutWithStimulus({ layout }) {
        const { BASIC_LEFT, VERTICAL_LEFT, VERTICAL_TOP } = uiConstants.SECTION_LAYOUT;
        return [BASIC_LEFT, VERTICAL_LEFT, VERTICAL_TOP].includes(layout);
    },

    isLeftSectionLayout({ layout }) {
        const { BASIC_LEFT, VERTICAL_LEFT } = uiConstants.SECTION_LAYOUT;
        return [BASIC_LEFT, VERTICAL_LEFT].includes(layout);
    },

    isBasicSectionLayout({ layout }) {
        const { BASIC, BASIC_LEFT } = uiConstants.SECTION_LAYOUT;
        return [BASIC, BASIC_LEFT, undefined].includes(layout);
    },

    isTopStimulusPosition(stimulusPosition) {
        return stimulusPosition === uiConstants.STIMULUS_POSITION.TOP;
    },

    isQuestionStimulusHolder(stimulusHolder) {
        return stimulusHolder === uiConstants.STIMULUS_HOLDER.QUESTION;
    },

    scaleImage(imageId, zoomFactor) {
        if (zoomFactor === 1) {
            return;
        }

        const image = document.querySelector(`img[data-content-id="${imageId}"]:not(.scaled)`);
        if (image) {
            const { offsetWidth, offsetHeight } = image;
            image.style.width = `${offsetWidth * zoomFactor}px`;
            image.classList.add('scaled');

            if (offsetHeight > 0 && image.style.height !== 'auto') {
                image.style.height = `${offsetHeight * zoomFactor}px`;
            }
        }
    },

    shouldAddExtraLineSpacing(question) {
        return themeServices.getThemeInstance().shouldAddExtraLineSpacing(question);
    },

    hexToRgba(color) {
        if (!color) return false;

        const [r, g, b] = color.match(/\w\w/g).map(x => parseInt(x, 16));
        return `rgba(${r}, ${g}, ${b}, 0.6)`;
    },

    isInIframe() {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    },

    areQuestionSectionsFocusable() {
        return marketplacePropertiesService.getA11yIterationForRegions() > 0;
    },

    getQuestionRegionsAccessibilityAttributes() {
        if (!this.areQuestionSectionsFocusable()) {
            return {};
        }

        return {
            role: 'region',
            tabIndex: 0,
        };
    },

    showBackgroundAudioControlButton() {
        return navigationService.isQuestionPage() && backgroundAudioService.isBackgroundAudioControllable();
    },
};

export default uiService;
